import React, { useState, useEffect } from "react";
import { FilterModal } from "@component/candidate-filter-modal";
import { Button } from "@component/button";
import {
  SearchIcon,
  XIcon,
  ClipboardIcon,
  Loader2Icon,
  AlertCircleIcon,
  PlusIcon,
  ChevronDown,
  BriefcaseIcon,
  ArchiveIcon,
  LayoutGrid,
  List,
} from "lucide-react";
import { updateJob, deleteJob, closeJob, getListCompanies, getJobListing, getJobCounts } from "@service/job";
import { Job, JobsFilterState, JobSortState } from "@type/job";
import { JobCard } from "@component/job-card";
import { formatHumanReadable } from "@util/date";
import { getStatusBadgeClass } from "@util/string";
import { MetaPagination } from "@type/common";
import { convertCurrency } from "@util/currency";
import { useUserProfileContext } from "@context/user_profile";
import { defaultPaginationJob } from "@constant/app";
import { EmptyState } from "@component/empty-state";
import { Input } from "@component/input";
import { Company } from "@type/company";
import { useNavigate } from "react-router-dom";
import LoadingBox from "@component/loading-box";
import SortPanel from "@component/job-sort-filter";
import { cn } from "@util/tailwind";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import JobFilterV2 from "@component/job-filter-v2";
import { JobTableView } from "@component/job-table-view";
import { useSubscription } from "@context/subscription_context";
import { Badge } from "@component/badge";

const DEFAULT_PAGINATION = {
  page: defaultPaginationJob.page,
  limit: defaultPaginationJob.limit,
  total: 0,
  total_pages: 0
}

const TAB_DEFAULT_STATUS: Record<string, string[]> = {
  jobs: ["Published"],
  inactive: ["Paused", "Closed"],
};

const DEFAULT_FILTER: JobsFilterState = {
  jobStatus: [],
  workArrangement: [],
  commitmentType: [],
  country: [],
  company: [],
  department: [],
};

const getFilterStorageKey = (tab: string) => `jobFilter_${tab}`;

export function JobDirectory() {
  const navigate = useNavigate();
  const [isLoadingUserProfile, userInfo] = useUserProfileContext();
  const { isJobActionBlocked } = useSubscription();
  const [showFilters, setShowFilters] = useState(false);
  const [sortOptions, setSortOptions] = useState<JobSortState>(() => {
    const saved = localStorage.getItem("jobSortOptions");
    return saved ? JSON.parse(saved) : { job_date: "", job_title: "" };
  });
  const [filterCompany, setFilterCompany] = useState<string | undefined>("All Companies");
  const [isOpenDropdownCompany, setOpenDropdownCompany] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keywords, setKeywords] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [showJobViewModal, setShowJobViewModal] = useState(false);
  const [jobToView, setJobToView] = useState<Job | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDelete, setLoadingDelete] = useState(false);
  const [paginationMetadata, setPaginationMetadata] = useState<MetaPagination>(DEFAULT_PAGINATION)
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("tab") || "jobs";
  });
  const [selectedFilterJob, setSelectedFilterJob] = useState<JobsFilterState>(() => {
    const saved = localStorage.getItem(getFilterStorageKey(activeTab));
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch { /* ignore */ }
    }
    const defaultStatus = TAB_DEFAULT_STATUS[activeTab] || ["Published"];
    return { ...DEFAULT_FILTER, jobStatus: defaultStatus };
  });
  const [jobCounts, setJobCounts] = useState<{ active: number; inactive: number }>({ active: 0, inactive: 0 });
  const [viewMode, setViewMode] = useState<"card" | "table">(() => {
    const saved = localStorage.getItem("jobViewMode");
    return saved === "card" ? "card" : "table";
  });

  const getJobStatusString = (filterOverride?: JobsFilterState): string => {
    const filter = filterOverride || selectedFilterJob;
    const statuses = filter.jobStatus.length > 0
      ? filter.jobStatus
      : (TAB_DEFAULT_STATUS[activeTab] || ["Published"]);
    return statuses.join(",");
  };

  useEffect(() => {
    if (!isLoadingUserProfile && ["admin", "super_admin"].includes(userInfo?.role || "")) {
      fetchAllCompanies();
    }
  }, [isLoadingUserProfile, userInfo]);

  useEffect(() => {
    if (!isLoadingUserProfile && userInfo) {
      fetchJobCounts();
    }
  }, [isLoadingUserProfile, userInfo]);

  useEffect(() => {
    const defaultStatus = TAB_DEFAULT_STATUS[activeTab] || ["Published"];
    const resetFilter = { ...selectedFilterJob, jobStatus: defaultStatus };
    setSelectedFilterJob(resetFilter);
    setCurrentPage(defaultPaginationJob.page);
    setKeywords("");
    setHasSearched(false);
    fetchJobs(defaultPaginationJob.page, false, { keyword: "", filterJob: resetFilter });
  }, [activeTab])

  useEffect(() => {
    localStorage.setItem("jobViewMode", viewMode);
  }, [viewMode]);

  const handleOnFiltersChange = (newFilters: JobsFilterState) => {
    setSelectedFilterJob(newFilters);
    localStorage.setItem(getFilterStorageKey(activeTab), JSON.stringify(newFilters));
    fetchJobs(defaultPaginationJob.page, false, { filterJob: newFilters });
  }

  const handleOnSortChange = (newSortOptions: JobSortState) => {
    setSortOptions(newSortOptions);
    fetchJobs(defaultPaginationJob.page, false, { sortOpts: newSortOptions });
  }

  const fetchAllCompanies = async () => {
    getListCompanies().then((response) => {
      if (response.status === 200) {
        setCompanies(response.data.data);
      }
    });
  }

  const fetchJobCounts = async () => {
    try {
      const counts = await getJobCounts();
      setJobCounts(counts);
    } catch (err) {
      console.error("Failed to fetch job counts:", err);
    }
  }

  const fetchJobs = async (
    page: number,
    isLoadMore: boolean,
    opts?: {
      keyword?: string;
      sortOpts?: JobSortState;
      companyName?: string;
      filterJob?: JobsFilterState;
    }
  ) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoadingJobs(true);
      }

      const currentSort = opts?.sortOpts || sortOptions;
      const currentCompany = opts?.companyName ?? filterCompany;
      const currentKeyword = opts?.keyword ?? keywords;

      let sortBy: string[] = [];
      let sort: string[] = [];
      if (currentSort?.job_title) {
        sortBy = [...sortBy, "job_title"];
        sort = [...sort, currentSort.job_title];
      }
      if (currentSort?.job_date) {
        sortBy = [...sortBy, "job_date"];
        sort = [...sort, currentSort.job_date];
      }

      let jobTypes = "byoc,talent-pool";
      if (userInfo?.role === "hr") {
        jobTypes = "";
      }

      const currentFilter = opts?.filterJob || selectedFilterJob;

      setError(null);
      const resp = await getJobListing({
        keyword: currentKeyword,
        page: page,
        limit: defaultPaginationJob.limit,
        job_status: getJobStatusString(opts?.filterJob),
        job_visibility: "",
        job_type: jobTypes,
        filter_company: currentCompany === "All Companies" ? "" : currentCompany,
        sort_by: sortBy.join(",") || "",
        sort: sort.join(",") || "",
        work_arrangement: currentFilter.workArrangement.join(",") || "",
        commitment_type: currentFilter.commitmentType.join(",") || "",
        country: currentFilter.country.join(",") || "",
        company: currentFilter.company.join(",") || "",
        department: currentFilter.department.join(",") || "",
      })

      if (resp.status === 200) {
        if (isLoadMore) {
          setJobs((prevJobs: Job[]) => {
            return [...prevJobs, ...resp.data.data];
          });
        } else {
          setJobs(resp.data.data);
        }
      }

      setPaginationMetadata(resp?.data?.meta?.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setError("Failed to load job listings. Please try again later.");
      setPaginationMetadata(DEFAULT_PAGINATION);
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoadingJobs(false);
      }
    }
  };

  const editJobStatus = async (jobId: string, status: string, is_public?: boolean | undefined) => {
    try {
      setError(null);
      await updateJob(jobId, { job_status: status, is_reopened: status === "Published" ? true : undefined, is_public: is_public });
      await fetchJobs(defaultPaginationJob.page, false);
      fetchJobCounts();
    } catch (err) {
      console.error("Failed to update job status:", err);
      setError("Failed to update job status. Please try again later.");
    }
  }

  const openJobViewModal = (job: Job) => {
    navigate(`/jobs/edit/${job.id}`);
  };

  const closeJobViewModal = () => {
    setJobToView(null);
    setShowJobViewModal(false);
  };

  const openCreateJobModal = () => {
    navigate("/jobs/create");
  }

  const confirmCloseJob = async (job: Job) => {
    try {
      setError(null);

      const success = await closeJob(job.id);
      if (success) {
        await fetchJobs(defaultPaginationJob.page, false);
        setCurrentPage(defaultPaginationJob.page);
        fetchJobCounts();
      } else {
        setError("Failed to close job. Please try again later.");
      }
    } catch (err) {
      console.error("Failed to close job:", err);
      setError("Failed to close job. Please try again later.");
    }
  };

  const confirmDeleteJob = async (job: Job) => {
    setLoadingDelete(true)

    try {
      setError(null);

      const success = await deleteJob(job.id);
      if (success) {
        await fetchJobs(defaultPaginationJob.page, false);
        setCurrentPage(defaultPaginationJob.page);
        fetchJobCounts();
      } else {
        setError("Failed to delete job. Please try again later.");
      }
    } catch (err) {
      console.error("Failed to delete job:", err);
      setError("Failed to delete job. Please try again later.");
    } finally {
      setLoadingDelete(false)
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSearching(true);
      setError(null);

      const resp = await getJobListing({
        page: defaultPaginationJob.page,
        limit: defaultPaginationJob.limit,
        keyword: keywords,
        job_status: getJobStatusString(),
        job_visibility: "",
        filter_company: filterCompany === "All Companies" ? "" : (filterCompany || ""),
        work_arrangement: selectedFilterJob.workArrangement.join(",") || "",
        commitment_type: selectedFilterJob.commitmentType.join(",") || "",
        country: selectedFilterJob.country.join(",") || "",
        company: selectedFilterJob.company.join(",") || "",
        department: selectedFilterJob.department.join(",") || "",
      })
      if (resp.status === 200) {
        setJobs(resp.data.data);
        setPaginationMetadata(resp.data.meta.pagination);
        setHasSearched(true);
      }
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setError("Failed to load job listings. Please try again later.");
    } finally {
      setIsSearching(false);
    }
  };

  const onChangeSearch = async (value: string) => {
    setKeywords(value)

    if (hasSearched && value === "") {
      setHasSearched(false);

      try {
        setIsSearching(true);
        setError(null);

        const resp = await getJobListing({
          page: defaultPaginationJob.page,
          limit: defaultPaginationJob.limit,
          keyword: "",
          job_status: getJobStatusString(),
          job_visibility: "",
          filter_company: filterCompany === "All Companies" ? "" : (filterCompany || ""),
          work_arrangement: selectedFilterJob.workArrangement.join(",") || "",
          commitment_type: selectedFilterJob.commitmentType.join(",") || "",
          country: selectedFilterJob.country.join(",") || "",
          company: selectedFilterJob.company.join(",") || "",
          department: selectedFilterJob.department.join(",") || "",
        })
        if (resp.status === 200) {
          setJobs(resp.data.data);
          setPaginationMetadata(resp.data.meta.pagination);
          setHasSearched(true);
        }
      } catch (err) {
        console.error("Failed to fetch jobs:", err);
        setError("Failed to load job listings. Please try again later.");
      } finally {
        setIsSearching(false);
      }
    }
  }

  const onClearSearch = async () => {
    setKeywords("")

    if (hasSearched) {
      setHasSearched(false);
    }

    try {
      setIsSearching(true);
      setError(null);

      const resp = await getJobListing({
        page: defaultPaginationJob.page,
        limit: defaultPaginationJob.limit,
        keyword: "",
        job_status: getJobStatusString(),
        job_visibility: "",
        filter_company: filterCompany === "All Companies" ? "" : (filterCompany || ""),
        work_arrangement: selectedFilterJob.workArrangement.join(",") || "",
        commitment_type: selectedFilterJob.commitmentType.join(",") || "",
        country: selectedFilterJob.country.join(",") || "",
        company: selectedFilterJob.company.join(",") || "",
        department: selectedFilterJob.department.join(",") || "",
      })
      if (resp.status === 200) {
        setJobs(resp.data.data);
        setPaginationMetadata(resp.data.meta.pagination);
        setHasSearched(true);
      }
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      setError("Failed to load job listings. Please try again later.");
    } finally {
      setIsSearching(false);
    }
  }

  const handleLoadMore = async () => {
    setCurrentPage((prev) => prev + 1);
    await fetchJobs(currentPage + 1, true);
  };

  const handleCompanyChange = (value: string) => {
    setFilterCompany(value);
    fetchJobs(defaultPaginationJob.page, false, { companyName: value });
  }

  const onChangeTab = (tab: string) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.pushState({}, "", url.toString());
  }

  return (
    <>
      <Tabs defaultValue="jobs" value={activeTab} onValueChange={onChangeTab} className="w-full">
        <div className="flex justify-between items-center mb-4">
          <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
            <TabsTrigger
              value="jobs"
              className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
            >
              <BriefcaseIcon className="h-4 w-4" />
              Active Jobs
              <Badge
                variant="secondary"
                className={cn(
                  "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                  activeTab === "jobs"
                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                )}
              >
                {jobCounts.active}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="inactive"
              className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
            >
              <ArchiveIcon className="h-4 w-4" />
              Inactive Jobs
              <Badge
                variant="secondary"
                className={cn(
                  "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                  activeTab === "inactive"
                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                )}
              >
                {jobCounts.inactive}
              </Badge>
            </TabsTrigger>
          </TabsList>
          {((activeTab === "jobs" || activeTab === "inactive") && !isLoadingUserProfile && userInfo && userInfo.role === "hr" && userInfo.permission !== "member") && (
            <Button
              size="sm"
              className="flex items-center gap-1"
              disabled={isJobActionBlocked}
              onClick={(e) => { e.stopPropagation(); openCreateJobModal() }}
            >
              <PlusIcon className="h-4 w-4" />
              Create Job
            </Button>
          )}
        </div>

        {/* Search bar and filters */}
        {(activeTab === "jobs" || activeTab === "inactive") && (
          <div className="flex flex-col space-y-4 mb-4">
            <div className="flex flex-row flex-wrap gap-2">
              <form onSubmit={handleSearchSubmit} className="relative flex-1">
                <div className="relative flex-grow">
                  <SearchIcon
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                  />

                  <Input
                    className="pl-9 w-full"
                    placeholder="Try searching 'Software Engineer', 'Marketing Manager' etc."
                    value={keywords}
                    onChange={(e) => onChangeSearch(e.target.value)}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2Icon className="h-4 w-4 text-blue-600 animate-spin" />
                    </div>
                  )}

                  {hasSearched && !isSearching && (
                    <div className={cn("absolute top-1/2 transform -translate-y-1/2 text-sm text-gray-500", {
                      "right-12": keywords !== "",
                      "right-3": keywords === "",
                    })}>
                      displaying {paginationMetadata.total} results
                    </div>
                  )}

                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {!isSearching && keywords && (
                      <button
                        type="button"
                        onClick={onClearSearch}
                        className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                        aria-label="Clear search"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <button type="submit" className="hidden">Find</button>
              </form>
              <div className="flex sm:flex-row flex-col gap-2">
                <JobFilterV2
                  filters={selectedFilterJob}
                  isInActiveTab={activeTab === "inactive"}
                  onFiltersChange={handleOnFiltersChange}
                />
                <SortPanel
                  optionName={activeTab === "inactive" ? "inactiveJobSortOptions" : "jobSortOptions"}
                  sortOptions={sortOptions}
                  onSortChange={handleOnSortChange}
                />
                <div className="flex items-center border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-9 w-9 rounded-r-none",
                      viewMode === "table" && "bg-gray-100"
                    )}
                    onClick={() => setViewMode("table")}
                    aria-label="Table view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-9 w-9 rounded-l-none border-l",
                      viewMode === "card" && "bg-gray-100"
                    )}
                    onClick={() => setViewMode("card")}
                    aria-label="Card view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {["admin", "super_admin"].includes(userInfo?.role || "") && (
                <div className="flex sm:flex-row flex-col gap-2">
                  <div className="relative dropdown-container flex-1">
                    <Button
                      variant="outline"
                      className="flex items-center justify-between sm:w-48 text-left pl-3 relative w-full"
                      onClick={() => setOpenDropdownCompany(isOpenDropdownCompany === "company" ? null : "company")}
                    >
                      <span>{companies.find(company => company.id === filterCompany)?.company_name || "All Companies"}</span>
                      <ChevronDown size={16} className="ml-2" />
                    </Button>
                    {isOpenDropdownCompany === "company" && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                        <div className="py-1">
                          <div
                            className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer bg-gray-50 font-medium`}
                            onClick={() => handleCompanyChange("All Companies")}
                          >
                            All Companies
                          </div>
                          {companies.map((company) => (
                            <div
                              key={company.id}
                              className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${filterCompany === company.id
                                ? "bg-gray-50 font-medium"
                                : ""
                                }`}
                              onClick={() =>
                                handleCompanyChange(company.id)
                              }
                            >
                              {company.company_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Job listing content */}
        {(activeTab === "jobs" || activeTab === "inactive") && (
          <div className="space-y-4">
            {showFilters && <FilterModal onClose={toggleFilters} onApply={(filters) => {
              console.log('Applied filters:', filters);
              toggleFilters();
            }} />}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center">
                <AlertCircleIcon className="h-5 w-5 mr-2" />
                {error}
              </div>
            )}
            <div className="bg-white overflow-hidden">
              {isLoadingJobs ? (
                <LoadingBox />
              ) : jobs.length === 0 ? (
                <EmptyState
                  icon={(
                    <ClipboardIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                  )}
                  title="No jobs found matching your criteria"
                  description="Try adjusting your or search terms to find what you're looking for."
                />
              ) : viewMode === "card" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobs.map((job, index) => (
                    <JobCard
                      key={index}
                      job={job}
                      onJobAction={openJobViewModal}
                      onStatusChange={editJobStatus}
                      onCloseJob={(job: Job) => {
                        confirmCloseJob(job);
                      }}
                      onDelete={(job: Job) => {
                        confirmDeleteJob(job);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <JobTableView
                  jobs={jobs}
                  onJobAction={openJobViewModal}
                  onStatusChange={editJobStatus}
                  onCloseJob={(job: Job) => {
                    confirmCloseJob(job);
                  }}
                  onDelete={(job: Job) => {
                    confirmDeleteJob(job);
                  }}
                />
              )}
            </div>

            {(jobs || []).length > 0 &&
              currentPage < paginationMetadata.total_pages && (
                <div className="mt-6 flex justify-center">
                  <Button
                    variant="outline"
                    className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                        Loading...
                      </div>
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}

          </div>
        )}
      </Tabs>


      {showJobViewModal && jobToView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Job Details
                </h2>
                <button
                  onClick={closeJobViewModal}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <XIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{jobToView.job_title}</h3>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    {jobToView.department && <span className="mr-2">{jobToView.department}</span>}
                    {jobToView.location && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="mr-2">{jobToView.location}</span>
                      </>
                    )}
                    {jobToView.job_type && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{jobToView.job_type}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex space-x-4 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(jobToView.job_status)}`}>
                    {jobToView.job_status}
                  </span>

                  <span className="text-sm text-gray-500">
                    Posted on {formatHumanReadable(jobToView.created_at, true)}
                  </span>

                  <span className="text-sm text-gray-500">
                    {jobToView?.applicants_count || 0} applicant{jobToView?.applicants_count !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="mt-4 border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Description</h4>
                  <div className="text-black" dangerouslySetInnerHTML={{ __html: jobToView.job_description || "" }} />
                </div>

                {jobToView.requirements && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Requirements</h4>
                    <div className="text-black" dangerouslySetInnerHTML={{ __html: jobToView.requirements || "" }} />
                  </div>
                )}

                {jobToView.benefits && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Benefits</h4>
                    <div className="text-black" dangerouslySetInnerHTML={{ __html: jobToView.benefits || "" }} />
                  </div>
                )}

                {(jobToView.min_rate > 0 || jobToView.max_rate > 0) && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Salary Range</h4>
                    <p className="text-gray-700">
                      {jobToView.min_rate > 0 && `${convertCurrency(jobToView.min_rate, jobToView.rate_currency || "USD")}`}
                      {' - '}
                      {jobToView.max_rate > 0 && `${convertCurrency(jobToView.max_rate, jobToView.rate_currency || "USD")}`}
                    </p>
                  </div>
                )}

                {(jobToView.fixed_rate > 0) && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Salary</h4>
                    <p className="text-gray-700">
                      {jobToView.fixed_rate > 0 && `${convertCurrency(jobToView.fixed_rate, jobToView.rate_currency || "USD")}`}
                    </p>
                  </div>
                )}

                {jobToView.referral_bonus > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Referral Bonus</h4>
                    <p className="text-gray-700">
                      {jobToView.referral_bonus > 0 && `${convertCurrency(jobToView.referral_bonus, jobToView.rate_currency || "USD")}`}
                    </p>
                  </div>
                )}

                {jobToView.assessment_ids && jobToView.assessment_ids.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Screenings</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {jobToView.assessment_ids.map((assessment) => (
                        <li key={assessment.id} className="text-gray-700">
                          <b>[{assessment.type}]</b> {assessment.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={closeJobViewModal}
                >
                  Close
                </Button>
                <Button
                  variant="default"
                  disabled={isJobActionBlocked}
                  onClick={() => {
                    navigate(`/jobs/edit/${jobToView.id}`);
                  }}
                >
                  Edit Job
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
